import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download } from "lucide-react";
import axios from "axios";
import { downloadCatalog } from "@/lib/catalogDownload";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  "California - Los Angeles",
  "California - Santa Clara",
  "Colorado - Denver",
  "Nevada - Las Vegas",
  "Other / Not Listed",
];

export default function CatalogModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", state: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [routed, setRouted] = useState("");
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.state) {
      setErr("Name, email and location are required.");
      return;
    }
    setStatus("sending");
    try {
      const { data } = await axios.post(`${BACKEND}/api/catalog-lead`, form);
      setRouted(data.routed_showroom || "");
      setStatus("done");
      downloadCatalog(data.catalog_url);
    } catch (e2) {
      setStatus("error");
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="catalog-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-brand-bone shadow-2xl overflow-hidden"
            data-testid="catalog-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-brand-ink/60 hover:text-brand-green transition"
              data-testid="catalog-modal-close"
            >
              <X size={22} />
            </button>
            <div className="p-8 md:p-10">
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold mb-3 font-medium">
                Atelier · Full Catalog
              </div>
              <h3 className="font-light text-brand-green text-2xl md:text-3xl leading-tight tracking-[-0.01em]">
                Download the Atelier Doors catalog.
              </h3>
              <p className="text-brand-ink/70 mt-3 text-sm font-light leading-relaxed">
                A private introduction from the showroom nearest to you.
              </p>

              {status === "done" ? (
                <div className="mt-6 border border-brand-gold/40 bg-brand-gold/10 px-5 py-4 text-brand-green" data-testid="catalog-success">
                  <p className="font-medium">Your catalog is downloading.</p>
                  <p className="text-sm mt-1 text-brand-ink/75">
                    A specialist from our <strong>{routed}</strong> showroom will follow up shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="mt-6 space-y-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-brand-ink/15 focus:border-brand-gold outline-none text-sm text-brand-ink placeholder:text-brand-ink/50"
                    data-testid="catalog-input-name"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-brand-ink/15 focus:border-brand-gold outline-none text-sm text-brand-ink placeholder:text-brand-ink/50"
                    data-testid="catalog-input-email"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-brand-ink/15 focus:border-brand-gold outline-none text-sm text-brand-ink placeholder:text-brand-ink/50"
                    data-testid="catalog-input-phone"
                  />
                  <select
                    value={form.state}
                    onChange={(e) => set("state", e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-brand-ink/15 focus:border-brand-gold outline-none text-sm text-brand-ink"
                    data-testid="catalog-input-state"
                    required
                  >
                    <option value="" disabled>Nearest Showroom / Location…</option>
                    {STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {err && (
                    <p className="text-red-700 text-xs" data-testid="catalog-error">{err}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="btn-primary w-full justify-center disabled:opacity-60"
                    data-testid="catalog-submit"
                  >
                    <Download size={16} />
                    {status === "sending" ? "Preparing…" : "Download Catalog"}
                  </button>
                  <p className="text-[0.62rem] tracking-[0.14em] uppercase text-brand-ink/50 text-center">
                    Your details go directly to the nearest showroom.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
